import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Music2 } from "lucide-react";
import { LOGO_PINK } from "../../lib/data";
import { EmailCapture } from "../common/EmailCapture";
import { api } from "../../lib/api";

const COLS = [
    {
        title: "Shop",
        links: [
            { label: "Shop All", to: "/shop" },
            { label: "Party Packs", to: "/party-packs" },
            { label: "Flavors", to: "/flavors" },
        ],
    },
    {
        title: "Company",
        links: [
            { label: "Book an Event", to: "/book" },
            { label: "Wholesale", to: "/wholesale" },
            { label: "Retailer Login", to: "/wholesale" },
            { label: "About", to: "/about" },
        ],
    },
    {
        title: "Support",
        links: [
            { label: "FAQ", to: "/faq" },
            { label: "Contact", to: "/contact" },
            { label: "Shipping & Delivery", to: "/shipping" },
            { label: "Returns & Refunds", to: "/returns" },
        ],
    },
];

const LEGAL = [
    { label: "Privacy", to: "/privacy" },
    { label: "Terms", to: "/terms" },
    { label: "Accessibility", to: "/accessibility" },
];

export const Footer = () => {
    const [socials, setSocials] = useState({ instagram_url: "", tiktok_url: "" });

    useEffect(() => {
        api.get("/settings/public")
            .then((res) => setSocials({ instagram_url: res.data?.instagram_url || "", tiktok_url: res.data?.tiktok_url || "" }))
            .catch(() => {});
    }, []);

    const Social = ({ url, label, icon: Icon, testid }) =>
        url ? (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testid}
                aria-label={label}
                className="h-11 w-11 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-300 hover:border-pink hover:text-pink transition-colors duration-300"
            >
                <Icon size={18} />
            </a>
        ) : (
            <span
                data-testid={testid}
                aria-label={`${label} — coming soon`}
                className="h-11 w-11 rounded-full border border-zinc-800 flex items-center justify-center text-zinc-600 cursor-default"
                title="Coming soon"
            >
                <Icon size={18} />
            </span>
        );

    return (
        <footer data-testid="site-footer" className="bg-ink tent-stripes text-white">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10 pt-20 pb-28 md:pb-12">
                <div className="grid gap-14 lg:grid-cols-[1.3fr_2fr]">
                    <div>
                        <img src={LOGO_PINK} alt="Showtime Carnival Co." className="h-24 w-auto mb-6" data-testid="footer-logo" />
                        <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mb-8">
                            Fresh cotton candy, gourmet popcorn and snack experiences. Made in NYC, built for good times.
                        </p>
                        <p className="font-oswald uppercase tracking-[0.3em] text-xs text-pink mb-3">Get on the guest list</p>
                        <EmailCapture source="footer" dark testid="footer-newsletter" />
                        <div className="flex gap-3 mt-8">
                            {socials.instagram_url && (
                                <Social url={socials.instagram_url} label="Instagram" icon={Instagram} testid="footer-instagram" />
                            )}
                            {socials.tiktok_url && (
                                <Social url={socials.tiktok_url} label="TikTok" icon={Music2} testid="footer-tiktok" />
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-10">
                        {COLS.map((col) => (
                            <div key={col.title}>
                                <h3 className="font-oswald uppercase tracking-[0.25em] text-sm text-pink mb-5">{col.title}</h3>
                                <ul className="space-y-3">
                                    {col.links.map((link) => (
                                        <li key={link.label}>
                                            <Link
                                                to={link.to}
                                                data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}
                                                className="text-sm text-zinc-300 hover:text-white transition-colors duration-300"
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="mt-16 pt-8 border-t border-zinc-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <p className="text-xs text-zinc-500">© 2026 Showtime Carnival Co.™ — New York City</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                        {LEGAL.map((link) => (
                            <Link
                                key={link.label}
                                to={link.to}
                                data-testid={`footer-legal-${link.label.toLowerCase()}`}
                                className="text-xs text-zinc-500 hover:text-pink transition-colors duration-300"
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
};
